import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export interface Language {
  _id?: string;
  code: string;
  name: string;
  nativeName?: string;
  dir: 'ltr' | 'rtl';
  enabled: boolean;
  isDefault: boolean;
  fallbackCode?: string;
  sortOrder?: number;
}

const SELECTED_KEY = 'lng.code';
const CACHE_KEY = 'lng.registry';

/**
 * The set of languages is served from the `languages` collection, not compiled
 * in — adding one is an admin action, never a release.
 *
 * The registry is cached in localStorage and read synchronously on construction
 * so the first paint has a direction and a language to work with. That cache is
 * the only local copy of the list; it is refreshed from the API on every boot
 * and is not a fallback list we maintain. On a first-ever load with no cache and
 * no API, `languages` is simply empty and ngx-translate keeps its default —
 * degraded, but never showing a language that is no longer configured.
 */
@Injectable()
export class LanguageService {
  constructor(
    public translate: TranslateService,
    private http: HttpClient,
  ) {
    this.languages = this.readCache();
    this.language = this.resolveSelected();
    this.applyLanguage(this.language);
  }

  languages: Language[] = [];

  language: Language | null;

  cssUrl: string;

  readonly languages$ = new BehaviorSubject<Language[]>([]);

  /**
   * Refreshes the registry from the API. Called at app start; safe to call
   * again after a language is added or enabled so the switcher updates without
   * a reload.
   */
  async load(): Promise<Language[]> {
    try {
      const languages = await firstValueFrom(this.http.get<Language[]>('api/languages/enabled'));
      this.languages = languages || [];
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.languages));
      this.languages$.next(this.languages);

      // The stored selection may have been disabled or removed since last visit.
      const resolved = this.resolveSelected();
      if (resolved && resolved.code !== this.language?.code) {
        this.setLanguage(resolved);
      } else {
        this.language = resolved;
        this.applyLanguage(this.language);
      }
    } catch {
      this.languages$.next(this.languages);
    }
    return this.languages;
  }

  getLanguage(): Language | null {
    return this.language;
  }

  setLanguage(language: Language | null) {
    if (!language) {
      return;
    }
    this.language = language;
    localStorage.setItem(SELECTED_KEY, language.code);
    this.applyLanguage(language);
  }

  private applyLanguage(language: Language | null) {
    // Layout rules key on body.ltr/.rtl, so the document must carry a
    // direction even when no language resolves (empty registry, first load
    // with no cache).
    const dir = language?.dir || 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.getElementsByTagName('body')[0].className = dir;
    // Direction stylesheet, not language content — one per writing direction,
    // so it stays correct for any language the registry gains.
    this.cssUrl = `assets/i18n/${dir}.css?k=${Math.random()}`;
    if (!language) {
      return;
    }
    document.documentElement.setAttribute('lang', language.code);
    this.translate.use(language.code);
  }

  /**
   * Stored code wins if it is still enabled, then the registry default, then the
   * first enabled language.
   */
  private resolveSelected(): Language | null {
    if (!this.languages.length) {
      return this.language ?? null;
    }
    const storedCode = localStorage.getItem(SELECTED_KEY);
    return (
      this.languages.find((language) => language.code === storedCode) ||
      this.languages.find((language) => language.isDefault) ||
      this.languages[0] ||
      null
    );
  }

  private readCache(): Language[] {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  }
}
